/**
 * To-scale figure for a reference docs page (`.doc`, globals.css ".doc layout" block):
 * one 28px segmented bar plus a legend. A segment's width is value / sum of all values,
 * so the picture can only say what the numbers say. Assigned segments step down an accent
 * ramp; `reserved` ones get a neutral hatch. Static — the items come from the page's
 * markdown table (see the `docs:scale-bar` directive in /docs-writer), never from a query.
 */
import type { CSSProperties } from 'react';

export interface DocScaleItem {
  label: string;
  value: number;
  /** Muted text between the label and the value in the legend (e.g. the CIDR range). */
  note?: string;
  /** `reserved` = held back, drawn as a hatch. Default `assigned`. */
  tone?: 'assigned' | 'reserved';
}

export interface DocScaleBarProps {
  caption: string;
  /** Text at the right of the caption (e.g. "32,768 addresses"). Default: the sum of the values. */
  total?: string;
  items: DocScaleItem[];
}

// Accent share (%) of the first and the last assigned segment; the rest are spread evenly.
const RAMP_FROM = 88;
const RAMP_TO = 34;

const fmt = (n: number) => n.toLocaleString('en-US');
const pct = (share: number) => `${Number((share * 100).toFixed(1))}%`;

export function DocScaleBar({ caption, total, items }: DocScaleBarProps) {
  const sum = items.reduce((n, it) => n + it.value, 0);
  const assigned = items.filter((it) => it.tone !== 'reserved').length;
  let step = 0;
  const rows = items.map((it) => {
    const reserved = it.tone === 'reserved';
    const mix = reserved
      ? undefined
      : RAMP_FROM - ((RAMP_FROM - RAMP_TO) * step++) / Math.max(assigned - 1, 1);
    return {
      ...it,
      reserved,
      share: sum > 0 ? it.value / sum : 0,
      ramp: mix === undefined ? undefined : ({ '--doc-seg': `${Math.round(mix)}%` } as CSSProperties),
    };
  });
  const shares = rows
    .map((r) => `${r.label} ${fmt(r.value)} (${pct(r.share)}${r.reserved ? ', reserved' : ''})`)
    .join(', ');

  return (
    <figure className="doc-scale">
      <figcaption className="doc-scale-cap">
        <span>{caption}</span>
        <span className="doc-scale-total">{total ?? fmt(sum)}</span>
      </figcaption>
      <div className="doc-scale-bar" role="img" aria-label={`${caption}. Shares: ${shares}.`}>
        {rows.map((r) => (
          <span
            key={r.label}
            className={r.reserved ? 'doc-scale-seg doc-scale-seg--reserved' : 'doc-scale-seg'}
            style={{ ...r.ramp, width: `${r.share * 100}%` }}
          />
        ))}
      </div>
      <ul className="doc-scale-legend">
        {rows.map((r) => (
          <li key={r.label}>
            <span
              className={r.reserved ? 'doc-scale-key doc-scale-key--reserved' : 'doc-scale-key'}
              style={r.ramp}
              aria-hidden="true"
            />
            <span className="doc-scale-label">{r.label}</span>
            <span className="doc-scale-note">{r.note}</span>
            <span className="doc-scale-value">{fmt(r.value)}</span>
          </li>
        ))}
      </ul>
    </figure>
  );
}
